"use client";

import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import gsap from "gsap";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
const testimonials = [{
  id: 1,
  quote: "Victoria Light Foundation turned my",
  highlight: "small",
  quoteEnd: "donation into real impact.",
  name: "Sarah Jenkins",
  role: "\n",
  label: "Member",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/sarah_jenkins_community_member_portrait.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/sarah_jenkins_community_member_portrait.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/sarah_jenkins_community_member_portrait.webp-wvc-sizes",
  alt: "Sarah Jenkins, foundation member, smiling warmly in a bright community setting"
}, {
  id: 2,
  quote: "The foundation gave me",
  highlight: "purpose",
  quoteEnd: "and drive to carry on after losing my business.",
  name: "David Williams",
  role: "Community Partner",
  label: "WELFARE PARTNER",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/david_williams_asian_community_partner_portrait.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/david_williams_asian_community_partner_portrait.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/david_williams_asian_community_partner_portrait.png-wvc-sizes",
  alt: "David Williams, an Asian community partner in his mid-forties, smiling warmly in a welcoming setting"
}, {
  id: 3,
  quote: "The transparency of the foundation makes me",
  highlight: "proud",
  quoteEnd: "to be a member.",
  name: "Elena Rossi",
  role: "\n",
  label: "Member",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/member_elena_rossi.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/member_elena_rossi.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/member_elena_rossi.png-wvc-sizes",
  alt: "Elena Rossi, a member, participating in a foundation event"
}, {
  id: 4,
  quote: "Never imagined there could be this much good in the world. Truly there's",
  highlight: "hope",
  quoteEnd: "for humanity.",
  name: "Marcus Thorne",
  role: "Foundation\xA0",
  label: "MEMBER",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/supporter_marcus_thorne_plain.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/supporter_marcus_thorne_plain.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/supporter_marcus_thorne_plain.webp-wvc-sizes",
  alt: "Marcus Thorne, a supporter, helping at a community outreach center"
}];
const handles = ["Making impact with excellence", "Making impact with excellence", "Making impact with excellence", "Making impact with excellence", "Making impact with excellence", "Making impact with excellence", "Making impact with excellence", "Making impact with excellence"];
export default function Voices() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const marqueeRef = useRef<HTMLDivElement | null>(null);
  const marqueeTweenRef = useRef<gsap.core.Tween | null>(null);
  const total = testimonials.length;
  const goTo = (nextIndex: number, dir: number) => {
    setDirection(dir);
    setIndex((nextIndex % total + total) % total);
  };
  const next = () => goTo(index + 1, 1);
  const prev = () => goTo(index - 1, -1);
  useEffect(() => {
    if (isPaused || prefersReducedMotion) return;
    const t = setTimeout(() => {
      setDirection(1);
      setIndex(i => (i + 1) % total);
    }, 7000);
    return () => clearTimeout(t);
  }, [index, isPaused, prefersReducedMotion, total]);
  useLayoutEffect(() => {
    if (!marqueeRef.current) return;
    if (prefersReducedMotion) return;
    const ctx = gsap.context(() => {
      const el = marqueeRef.current!;
      gsap.set(el, {
        xPercent: 0
      });
      marqueeTweenRef.current = gsap.to(el, {
        xPercent: -50,
        duration: 40,
        ease: "none",
        repeat: -1
      });
    }, marqueeRef);
    return () => ctx.revert();
  }, [prefersReducedMotion]);
  const active = testimonials[index];
  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: prefersReducedMotion ? 0 : dir * 24
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: prefersReducedMotion ? 0 : dir * -24
    })
  };
  return <section data-section-id="32093" id="voices-of-hope" data-nav="light" className="bg-background py-24 md:py-36">
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center rounded-lg bg-muted px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-foreground">
            Stories
          </span>
          <h2 className="mt-6 font-serif font-bold text-foreground" style={{
          fontSize: "clamp(2.5rem, 5.6vw, 5.25rem)",
          letterSpacing: "-0.03em",
          lineHeight: 0.94
        }}>
            Members share the{" "}
            <span className="italic" style={{
            color: "var(--secondary)"
          }}>
              impact
            </span>
          </h2>
        </div>

        <div className="relative mt-16" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocus={() => setIsPaused(true)} onBlur={() => setIsPaused(false)}>
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div key={active.id} data-index={index} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: [0.2, 0.7, 0.2, 1]
            }} className="rounded-3xl bg-muted p-6 md:p-10">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
                  <div className="md:col-span-5">
                    <div className="relative overflow-hidden rounded-2xl">
                      <img src={active.image} data-wvc-srcset={active.srcset} data-wvc-sizes={active.sizes} alt={active.alt} loading="lazy" decoding="async" className="h-[320px] w-full object-cover md:h-[400px]" />
                      <div className="absolute bottom-4 left-4">
                        <span className="inline-flex items-center rounded-lg bg-card px-3 py-1.5 text-[0.6875rem] font-medium uppercase text-foreground shadow" style={{
                        fontFamily: "var(--font-mono)",
                        letterSpacing: "0.04em"
                      }}>
                          {active.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center md:col-span-7">
                    <blockquote className="font-serif font-bold text-foreground" style={{
                    fontSize: "clamp(1.5rem, 3vw, 2.75rem)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1
                  }}>
                      {active.quote}{" "}
                      <span className="relative inline-block">
                        <span aria-hidden="true" className="absolute left-0 right-0 bottom-1 h-2 -z-0" style={{
                        backgroundColor: "var(--secondary)"
                      }} />
                        <span className="relative">{active.highlight}</span>
                      </span>{" "}
                      {active.quoteEnd}
                    </blockquote>
                    <div className="mt-8">
                      <div className="text-base font-semibold text-foreground">
                        {active.name}
                      </div>
                      <div className="text-base text-muted-foreground">
                        {active.role}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-10 flex items-center justify-between gap-6">
            <div className="flex flex-1 items-center gap-2">
              {testimonials.map((t, i) => <div key={t.id} data-index={i} className="relative h-px flex-1 overflow-hidden" style={{
              backgroundColor: "var(--border)"
            }}>
                  <motion.div className="absolute inset-y-0 left-0" style={{
                backgroundColor: "var(--foreground)"
              }} initial={{
                width: "0%"
              }} animate={{
                width: i === index ? "100%" : i < index ? "100%" : "0%"
              }} transition={{
                duration: 0.4,
                ease: [0.2, 0.7, 0.2, 1]
              }} />
                </div>)}
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={prev} aria-label="Previous testimonial" className="h-12 w-12 rounded-lg border-border bg-transparent text-foreground hover:bg-muted">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="icon" onClick={next} aria-label="Next testimonial" className="h-12 w-12 rounded-lg border-border bg-transparent text-foreground hover:bg-muted">
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-16 overflow-hidden" onMouseEnter={() => {
        if (marqueeTweenRef.current) marqueeTweenRef.current.pause();
      }} onMouseLeave={() => {
        if (marqueeTweenRef.current && !prefersReducedMotion) marqueeTweenRef.current.resume();
      }}>
          <div ref={marqueeRef} className="flex w-max items-center gap-10" style={{
          fontFamily: "var(--font-mono)",
          fontSize: "0.8125rem",
          letterSpacing: "0.04em"
        }}>
            {[...handles, ...handles].map((handle, i) => <span key={`${handle}-${i}`} data-index={i} className="whitespace-nowrap text-muted-foreground/60">
                {handle}
              </span>)}
          </div>
        </div>
      </div>
    </section>;
}